<template>
    <div class="bigSingleBetSlip notranslate">
        <header class="bigSingleBetSlip-header">
            <div
                class="bigSingleBetSlip-headerLeft"
                v-bind:class="[
                    `${gaClassPrefix}_Btn_Back`,
                    { _disabled: isBetSlipInteractionLocked },
                ]"
                v-bind:aria-disabled="isBetSlipInteractionLocked"
                v-on:click="returnToMiniBetSlip"
            >
                <SboIcon name="system:arrowBack" size="20" />
                <span class="bigSingleBetSlip-title" data-testid="big-single-title">
                    {{ i18n.t('bet_slip_header_name') }}
                </span>
            </div>
            <button
                class="bigSingleBetSlip-headerRight"
                v-bind:class="`${gaClassPrefix}_Btn_RemoveAll`"
                type="button"
                aria-label="Clear bet slip"
                v-bind:disabled="isBetSlipInteractionLocked"
                v-on:click="removeAllTickets"
            >
                <SboIcon name="system:trashOutline" size="20" />
            </button>
        </header>
        <div class="bigSingleBetSlip-main">
            <div class="bigSingleBetSlip-betGroup">
                <div class="bigSingleBetSlip-section">
                    <BetSlipTabs
                        v-bind:activeTab="activeTab"
                        v-bind:gaSizePrefix="getBetSlipGaSizePrefix('Big')"
                        v-bind:isDisabled="isBetSlipInteractionLocked"
                        v-on:switchTab="switchTab"
                    />
                    <div class="bigSingleBetSlip-scroll">
                        <MultipleSingleBetSlip
                            v-if="activeTab === SingleModeBetSlipTab.Single"
                            v-bind:gaClassPrefix="gaClassPrefix"
                        />
                        <template v-else>
                            <ParlayInSingleBetSlip
                                class="bigSingleBetSlip-parlayTickets"
                                v-bind:gaClassPrefix="gaClassPrefix"
                                v-on:addMoreBet="collapseBetSlipForAddMoreBet"
                            />
                            <div
                                v-if="!shouldShowParlayInsufficientMessage"
                                class="bigSingleBetSlip-addTicket"
                            >
                                <button
                                    class="bigSingleBetSlip-addTicketBtn"
                                    v-bind:class="`${gaClassPrefix}_Btn_AddMoreBet`"
                                    type="button"
                                    aria-label="Add ticket"
                                    v-bind:disabled="isBetSlipInteractionLocked"
                                    v-on:click="collapseBetSlipForAddMoreBet"
                                >
                                    <SboIcon name="system:plus" size="16" />
                                </button>
                            </div>
                            <ComboParlay
                                v-if="isShowCombo"
                                v-bind:gaClassPrefix="gaClassPrefix"
                                v-bind:isValid2MpSubBets="isValid2MpSubBets"
                                v-bind:options="comboParlayOptions"
                                v-bind:disabled="isBetSlipInteractionLocked"
                                v-on:toggle="addOrRemoveSelectedCombo"
                            />
                        </template>
                    </div>
                </div>
            </div>
        </div>
        <footer class="bigSingleBetSlip-footer">
            <template v-if="activeTab === SingleModeBetSlipTab.Single">
                <Message
                    v-if="footerErrorMessage"
                    status="error"
                    v-bind:messageKey="footerErrorMessage.messageKey"
                    v-bind:messageData="footerErrorMessage.messageData"
                    data-testid="big-single-footer-error"
                />
            </template>
            <SwitchVoucherRedeem
                v-if="isShowVoucherSwitch && activeTab === SingleModeBetSlipTab.Single"
                v-bind:gaClassPrefix="gaClassPrefix"
                v-bind:isRedeemVoucher="isVoucherRedemptionEnabled"
                v-bind:disabled="isBetSlipInteractionLocked"
                v-on:toggleRedeemVoucher="switchRedeemVoucherMode"
            />
            <Message
                v-if="activeTab === SingleModeBetSlipTab.Parlay && parlayFooterErrorMessage"
                status="error"
                v-bind:messageKey="parlayFooterErrorMessage.messageKey"
                v-bind:messageData="parlayFooterErrorMessage.messageData"
                data-testid="big-parlay-footer-error"
            />
            <div class="bigSingleBetSlip-summary">
                <div class="bigSingleBetSlip-summaryRow">
                    <span class="bigSingleBetSlip-summaryLabel">
                        {{ i18n.t('betSlip_totalBets') }}:
                    </span>
                    <span class="bigSingleBetSlip-summaryValue" data-testid="total-bets">
                        {{ summaryBetCount }}
                    </span>
                </div>
                <div class="bigSingleBetSlip-summaryRow">
                    <span class="bigSingleBetSlip-summaryLabel">
                        {{ i18n.t('bet_slip_total_stake') }}
                    </span>
                    <span class="bigSingleBetSlip-summaryValue" data-testid="total-stake">
                        {{ summaryStakeDisplay }}
                    </span>
                </div>
                <div class="bigSingleBetSlip-summaryRow">
                    <span class="bigSingleBetSlip-summaryLabel">
                        {{ i18n.t('bet_slip_total_max_payout') }}
                    </span>
                    <span class="bigSingleBetSlip-summaryValue _bold" data-testid="total-max-payout">
                        {{ summaryMaxPayoutDisplay }}
                    </span>
                </div>
            </div>
            <BetSlipCta
                v-if="activeTab === SingleModeBetSlipTab.Single"
                v-bind:gaClassPrefix="gaClassPrefix"
                v-bind:ctaAction="renderedCtaAction"
                v-bind:ticketCount="ctaTicketCount"
                v-bind:disabled="isRenderedCtaDisabled"
                v-bind:loading="isPlacingBet"
                v-on:click="handleRenderedCtaClick"
            />
            <template v-else>
                <div class="bigSingleBetSlip-parlayActions">
                    <StakeBox
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:disabled="isBetSlipInteractionLocked"
                        v-bind:stake="parlayStake"
                        v-bind:minBet="parlayMinBet"
                        v-bind:maxBet="parlayMaxBet"
                        v-bind:isInvalid="parlayStakeError !== null"
                        v-bind:isActive="isParlayStakePadOpen"
                        v-on:activate="openParlayStakePad"
                        v-on:updateStake="setParlayStake"
                    />
                    <BetSlipCta
                        class="bigSingleBetSlip-parlayPlaceBet"
                        v-bind:gaClassPrefix="gaClassPrefix"
                        v-bind:ctaAction="renderedParlayCtaAction"
                        v-bind:ticketCount="parlayCtaTicketCount"
                        v-bind:disabled="isRenderedParlayCtaDisabled"
                        v-bind:loading="isPlacingParlay"
                        v-on:click="handleRenderedParlayCtaClick"
                    />
                </div>
                <StakePad
                    v-if="isParlayStakePadOpen"
                    v-bind:gaClassPrefix="gaClassPrefix"
                    v-on:appendDigits="appendParlayStakeDigits"
                    v-on:addAmount="addParlayStakeAmount"
                    v-on:max="setMaxParlayStake"
                    v-on:backspace="backspaceParlayStake"
                    v-on:close="closeParlayStakePad"
                />
            </template>
            <SwitchAcceptAnyOddsValue
                v-if="!customerInfo.isAcceptAnyOdds"
                v-bind:gaClassPrefix="gaClassPrefix"
            />
        </footer>
    </div>
</template>

<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, onUnmounted } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import BetSlipCta from '@/components/betSlip/Single/BetSlipCta.vue';
    import BetSlipTabs from '@/components/betSlip/Single/BetSlipTabs.vue';
    import ComboParlay from '@/components/betSlip/Single/ComboParlay.vue';
    import MultipleSingleBetSlip from '@/components/betSlip/Single/MultipleSingleBetSlip.vue';
    import ParlayInSingleBetSlip from '@/components/betSlip/Single/ParlayInSingleBetSlip.vue';
    import StakeBox from '@/components/betSlip/Single/StakeBox.vue';
    import StakePad from '@/components/betSlip/Single/StakePad.vue';
    import Message from '@/components/common/Message.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import SwitchVoucherRedeem from '@/components/voucher/SwitchVoucherRedeem.vue';
    import { RoutePage, SingleModeBetSlipTab } from '@/interface/enum';
    import { getBetSlipGaClassPrefix, getBetSlipGaSizePrefix } from '@/libraries/betSlip/betSlipGaClass';
    import { useParlayInSingleCta } from '@/libraries/composables/useParlayInSingleCta';
    import { useSingleModeBetSlipCta } from '@/libraries/composables/useSingleModeBetSlipCta';
    import { i18n } from '@/libraries/i18n';
    import { enableScrolling } from '@/libraries/utils/dom';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useSingleModeBetSlipStore } from '@/stores/betSlip/singleModeBetSlipStore';

    const { showMiniBetSlip } = useBetSlipStore();
    const { customerInfo } = storeToRefs(useAccountStore());

    const {
        activeTab,
        isPlacingBet,
        isBetSlipInteractionLocked,
        isVoucherRedemptionEnabled,
        isShowVoucherSwitch,
        totalBetCount,
        totalStakeDisplay,
        totalMaxPayoutDisplay,
        parlayStake,
        isShowCombo,
        isValid2MpSubBets,
        comboParlayOptions,
        parlayTotalBetCount,
        parlayTotalStake,
        parlayStakeError,
        parlayMinBet,
        parlayMaxBet,
        parlayMaxPayout,
        isPlacingParlay,
        isParlayStakePadOpen,
        shouldShowParlayInsufficientMessage,
    } = storeToRefs(useSingleModeBetSlipStore());
    const {
        switchRedeemVoucherMode,
        removeAllTickets,
        switchTab,
        setParlayStake,
        appendParlayStakeDigits,
        addParlayStakeAmount,
        setMaxParlayStake,
        backspaceParlayStake,
        openParlayStakePad,
        closeParlayStakePad,
        addOrRemoveSelectedCombo,
    } = useSingleModeBetSlipStore();
    const {
        footerErrorMessage,
        ctaTicketCount,
        renderedCtaAction,
        isRenderedCtaDisabled,
        handleRenderedCtaClick,
    } = useSingleModeBetSlipCta();
    const {
        renderedParlayCtaAction,
        parlayCtaTicketCount,
        isRenderedParlayCtaDisabled,
        parlayFooterErrorMessage,
        handleRenderedParlayCtaClick,
    } = useParlayInSingleCta();
    const { formatMoneyWithSymbolAndDecimal } = useAccountStore();
    const router = useRouter();
    const route = useRoute();

    const gaClassPrefix = computed(() => getBetSlipGaClassPrefix('Big', activeTab.value));

    const isSingleTabActive = computed(() => activeTab.value === SingleModeBetSlipTab.Single);
    const summaryBetCount = computed(() => {
        if (isSingleTabActive.value) {
            return totalBetCount.value;
        }
        return parlayStake.value > 0 ? parlayTotalBetCount.value : 0;
    });
    const summaryStakeDisplay = computed(() => (
        isSingleTabActive.value
            ? totalStakeDisplay.value
            : formatMoneyWithSymbolAndDecimal(parlayTotalStake.value)
    ));
    const summaryMaxPayoutDisplay = computed(() => (
        isSingleTabActive.value
            ? totalMaxPayoutDisplay.value
            : formatMoneyWithSymbolAndDecimal(parlayMaxPayout.value)
    ));

    function returnToMiniBetSlip() {
        if (isBetSlipInteractionLocked.value) {
            return;
        }
        showMiniBetSlip();
    }

    function collapseBetSlipForAddMoreBet() {
        if (isBetSlipInteractionLocked.value) {
            return;
        }

        showMiniBetSlip();
        if (route.name !== RoutePage.Main && route.name !== RoutePage.MainBySport) {
            router.push({ name: RoutePage.Main });
        }
    }

    onUnmounted(() => {
        enableScrolling();
        closeParlayStakePad();
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/singleMode/bigSingleBetSlip";

:deep(.bigSingleBetSlip-parlayTickets) {
    display: block;
}
</style>
